import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  TreatmentPicker,
  type PickedTreatment,
} from "@/components/treatment-picker";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

const TARGET_KINDS = ["none", "treatment", "collection"] as const;

const schema = z.object({
  title: z.string().min(1, "Tytuł jest wymagany"),
  body: z.string().min(1, "Treść jest wymagana"),
  type: z.enum(["push", "email"]),
  scheduledFor: z.string().optional(),
  targetKind: z.enum(TARGET_KINDS),
  targetId: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

type Promotion = {
  id: string;
  title: string;
  body: string;
  type: "push" | "email";
  scheduledFor: Date | string | null;
  treatmentId?: string | null;
  collectionId?: string | null;
};

export function PromotionDialog({
  open,
  onOpenChange,
  promotion,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promotion?: Promotion | null;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!promotion;

  const { data: treatmentList } = trpc.treatment.list.useQuery(
    { page: 1, limit: 100 },
    { enabled: open },
  );
  const { data: collectionList } = trpc.collection.list.useQuery(undefined, {
    enabled: open,
  });

  const createMutation = trpc.promotion.create.useMutation({
    onSuccess: () => {
      utils.promotion.list.invalidate();
      toast.success("Notyfikacja utworzona");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.promotion.update.useMutation({
    onSuccess: () => {
      utils.promotion.list.invalidate();
      toast.success("Notyfikacja zaktualizowana");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      body: "",
      type: "push",
      scheduledFor: "",
      targetKind: "none",
      targetId: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    if (promotion) {
      const targetKind: (typeof TARGET_KINDS)[number] = promotion.treatmentId
        ? "treatment"
        : promotion.collectionId
          ? "collection"
          : "none";
      form.reset({
        title: promotion.title,
        body: promotion.body,
        type: promotion.type,
        scheduledFor: promotion.scheduledFor
          ? new Date(promotion.scheduledFor).toISOString().slice(0, 16)
          : "",
        targetKind,
        targetId:
          promotion.treatmentId ?? promotion.collectionId ?? "",
      });
    } else {
      form.reset({
        title: "",
        body: "",
        type: "push",
        scheduledFor: "",
        targetKind: "none",
        targetId: "",
      });
    }
  }, [open, promotion, form]);

  const watchTargetKind = form.watch("targetKind");
  const watchTargetId = form.watch("targetId");
  // Name of the treatment picked in this session; the picker searches
  // server-side, so the pick may lie outside the first page of the list.
  const [pickedTreatment, setPickedTreatment] =
    useState<PickedTreatment | null>(null);
  const selectedTreatmentName =
    watchTargetKind === "treatment" && watchTargetId
      ? (pickedTreatment?.id === watchTargetId
          ? pickedTreatment.name
          : treatmentList?.items.find((t) => t.id === watchTargetId)?.name) ??
        "wybrany zabieg"
      : undefined;

  const onSubmit = form.handleSubmit((data) => {
    const scheduledFor = data.scheduledFor
      ? new Date(data.scheduledFor).toISOString()
      : null;

    if (data.targetKind !== "none" && !data.targetId) {
      toast.error("Wybierz cel notyfikacji (zabieg lub kolekcję).");
      return;
    }

    const treatmentId =
      data.targetKind === "treatment" ? data.targetId || null : null;
    const collectionId =
      data.targetKind === "collection" ? data.targetId || null : null;

    if (isEdit && promotion) {
      updateMutation.mutate({
        id: promotion.id,
        title: data.title,
        body: data.body,
        scheduledFor,
        treatmentId,
        collectionId,
      });
    } else {
      createMutation.mutate({
        title: data.title,
        body: data.body,
        type: data.type,
        scheduledFor,
        treatmentId,
        collectionId,
      });
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edytuj notyfikację" : "Nowa notyfikacja"}
          </DialogTitle>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="p-title">Tytuł</Label>
              <Input id="p-title" {...form.register("title")} />
              {form.formState.errors.title && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-body">Treść wiadomości</Label>
              <Textarea id="p-body" rows={3} {...form.register("body")} />
              {form.formState.errors.body && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.body.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-type">Typ</Label>
                <select
                  id="p-type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...form.register("type")}
                  disabled={isEdit}
                >
                  <option value="push">Push notyfikacja</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-scheduled">Zaplanuj wysyłkę</Label>
                <Input
                  id="p-scheduled"
                  type="datetime-local"
                  {...form.register("scheduledFor")}
                />
                <p className="text-xs text-muted-foreground">
                  Puste = nie wysyłaj od razu
                </p>
              </div>
            </div>

            <div className="rounded-md border p-3 space-y-3 bg-muted/30">
              <div className="space-y-2">
                <Label>Cel — gdzie zabrać użytkownika po kliknięciu</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      value="none"
                      {...form.register("targetKind")}
                    />
                    Brak (otwórz aplikację)
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      value="treatment"
                      {...form.register("targetKind")}
                    />
                    Zabieg
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      value="collection"
                      {...form.register("targetKind")}
                    />
                    Kolekcja
                  </label>
                </div>
              </div>

              {watchTargetKind === "treatment" && (
                <div className="space-y-2">
                  <Label htmlFor="p-treatment">Zabieg</Label>
                  <p className="text-xs text-muted-foreground">
                    {selectedTreatmentName
                      ? `Wybrano: ${selectedTreatmentName}`
                      : "Nie wybrano zabiegu"}
                  </p>
                  <TreatmentPicker
                    id="p-treatment"
                    mode="single"
                    selectedIds={watchTargetId ? [watchTargetId] : []}
                    onPick={(id, treatment) => {
                      setPickedTreatment(treatment);
                      form.setValue("targetId", id, { shouldValidate: true });
                    }}
                    enabled={open}
                    listClassName="max-h-48"
                  />
                </div>
              )}

              {watchTargetKind === "collection" && (
                <div className="space-y-2">
                  <Label htmlFor="p-collection">Kolekcja</Label>
                  <select
                    id="p-collection"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                    {...form.register("targetId")}
                  >
                    <option value="">— wybierz —</option>
                    {collectionList?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit
                  ? "Zapisz zmiany"
                  : "Utwórz notyfikację"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
